import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import {
    createLegalCaseRecord,
    updateLegalCaseRecord,
    uploadFileToS3
} from '../../services/api/implementation/impl';
import { toOptions, withBlank } from './legalCase';
import {
    LEGAL_RECORDS,
    MAX_UPLOAD_BYTES,
    UPLOAD_TOO_LARGE_MESSAGE,
    recordFormValues,
    recordPayload,
    storageFileName
} from './legalRecords';
import { useLegalCaseOptions } from './legalCaseOptions';

// The API keeps "not recorded" apart from No, so the blank stays a choice.
const YES_NO_OPTIONS = [
    { label: 'Not recorded', value: '' },
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' }
];

/** Add or edit one hearing, follow-up or document. `row` is null for a new one. */
const LegalRecordModal = ({ task, kind, row = null, onClose }) => {
    const config = LEGAL_RECORDS[kind];
    const isNew = !row;
    const queryClient = useQueryClient();
    const options = useLegalCaseOptions();
    const { register, handleSubmit, watch, setValue } = useForm({
        defaultValues: recordFormValues(config, row)
    });
    const values = watch();
    // Files picked for `file` fields, by field key. They upload on Save, so a
    // cancelled form leaves nothing behind in S3.
    const [pickedFiles, setPickedFiles] = useState({});
    const [uploading, setUploading] = useState(false);

    const saveMutation = useMutation({
        mutationFn: (payload) =>
            isNew
                ? createLegalCaseRecord(task.id, config.path, payload)
                : updateLegalCaseRecord(task.id, config.path, row.id, payload),
        onSuccess: () => {
            toast.success(`${config.noun} ${isNew ? 'added' : 'updated'}`);
            queryClient.invalidateQueries(['managementTask']);
            onClose();
        },
        onError: (error) => {
            toast.error(
                error?.response?.data?.message || `Failed to save ${config.noun.toLowerCase()}`
            );
        }
    });

    const pickFile = (key, file) => {
        if (file && file.size > MAX_UPLOAD_BYTES) {
            toast.error(UPLOAD_TOO_LARGE_MESSAGE);
            return;
        }
        setPickedFiles((current) => ({ ...current, [key]: file || null }));
    };

    // Upload each picked file and put its S3 URL into its field.
    const withUploads = async (data) => {
        const next = { ...data };
        for (const [key, file] of Object.entries(pickedFiles)) {
            if (!file) continue;
            const stored = new File([file], storageFileName(task.id, file.name), { type: file.type });
            const response = await uploadFileToS3(stored);
            const url = response?.data?.files?.[0]?.presignedUrl;
            if (!url) throw new Error('The upload returned no file URL');
            next[key] = url;
        }
        return next;
    };

    const onSubmit = async (data) => {
        const error = config.validate?.(data);
        if (error) {
            toast.error(error);
            return;
        }
        let uploaded;
        setUploading(true);
        try {
            uploaded = await withUploads(data);
        } catch (uploadError) {
            toast.error(uploadError?.response?.data?.message || uploadError?.message || 'File upload failed');
            return;
        } finally {
            setUploading(false);
        }
        saveMutation.mutate(recordPayload(config, uploaded, { isNew }));
    };

    const renderField = (field) => {
        const isRequired = !!field.required || !!field.requiredIf?.(values);
        const common = {
            label: field.label,
            register,
            register_key: field.key,
            isRequired,
            width: 100
        };
        let input;
        if (field.type === 'checkbox') {
            input = (
                <label className="flex h-full items-end gap-2 pb-2 text-gray-600">
                    <input type="checkbox" className="h-4 w-4" {...register(field.key)} />
                    {field.label}
                </label>
            );
        } else if (field.type === 'file') {
            const current = values[field.key];
            const picked = pickedFiles[field.key];
            input = (
                <div className="flex flex-col gap-2 text-gray-700">
                    <span className="text-gray-600">{field.label}</span>
                    <input
                        type="file"
                        onChange={(e) => pickFile(field.key, e.target.files?.[0])}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-full file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {picked ? (
                        <p className="text-xs text-gray-500">Uploads on Save: {picked.name}</p>
                    ) : current ? (
                        <p className="flex items-center gap-3 text-xs">
                            <a
                                href={current}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#346C75] underline">
                                Current file
                            </a>
                            <button
                                type="button"
                                className="text-[#B42318] hover:underline"
                                onClick={() => setValue(field.key, '')}>
                                Remove
                            </button>
                        </p>
                    ) : null}
                </div>
            );
        } else if (field.type === 'yesno') {
            input = <FormInput {...common} type="dropdown" options={YES_NO_OPTIONS} />;
        } else if (field.type === 'dropdown') {
            const choices = toOptions(options[field.optionsKey]);
            input = (
                <FormInput
                    {...common}
                    type="dropdown"
                    options={isRequired ? choices : withBlank(choices, '—')}
                />
            );
        } else {
            input = <FormInput {...common} type={field.type} placeholder={field.placeholder} />;
        }
        return (
            <div key={field.key} className={field.wide ? 'col-span-2' : ''}>
                {input}
            </div>
        );
    };

    const busy = uploading || saveMutation.isPending;

    return (
        <Modal
            name={`${isNew ? 'Add' : 'Edit'} ${config.noun.toLowerCase()}`}
            width={60}
            onClose={onClose}
            hideClose={false}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {options.loaded ? (
                    <div className="grid grid-cols-2 gap-4">{config.fields.map(renderField)}</div>
                ) : (
                    <p className="text-sm text-gray-500">Loading…</p>
                )}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={!options.loaded || busy}
                        className="bg-[#346C75] text-white px-8 py-2 rounded hover:bg-[#2a565e] disabled:opacity-50"
                    >
                        {uploading ? 'Uploading...' : saveMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default LegalRecordModal;
